import { ItemSnapshot, Order, OrderSnapshot, Shipment } from "../types/DB2";

export type OrderSnapshotWithPreserved = OrderSnapshot & { preservedOrder?: Order };

const coerceDealerId = (candidate: unknown): string | null => {
  if (typeof candidate !== "string") return null;
  const trimmed = candidate.trim();
  return trimmed.length ? trimmed : null;
};

const pushDealerId = (target: string[], value: unknown, seen: Set<string>) => {
  const normalized = coerceDealerId(value);
  if (!normalized || seen.has(normalized)) {
    return;
  }
  seen.add(normalized);
  target.push(normalized);
};

const appendDealerIds = (target: string[], value: unknown, seen: Set<string>) => {
  if (Array.isArray(value)) {
    value.forEach((entry) => pushDealerId(target, entry, seen));
    return;
  }
  pushDealerId(target, value, seen);
};

type CollectDealerIdsOptions = {
  includeExisting?: boolean;
};

export const collectDealerIdsFromShipment = (
  shipment: Partial<Shipment> & {
    ordersSnapshot?: Record<string, any> | null;
    orders?: Array<Record<string, any>>;
  },
  options: CollectDealerIdsOptions = {}
): string[] => {
  const seen = new Set<string>();
  const collected: string[] = [];
  const includeExisting = options.includeExisting ?? true;

  if (includeExisting) {
    appendDealerIds(collected, (shipment as any)?.dealerIds, seen);
  }

  const snapshot = (shipment as any)?.ordersSnapshot;
  if (snapshot && typeof snapshot === "object") {
    Object.values(snapshot as Record<string, any>).forEach((orderSnapshot) => {
      if (!orderSnapshot || typeof orderSnapshot !== "object") return;
      pushDealerId(collected, (orderSnapshot as any).dealerId, seen);
      appendDealerIds(collected, (orderSnapshot as any).dealerIds, seen);

      const preserved = (orderSnapshot as any).preservedOrder;
      if (preserved && typeof preserved === "object") {
        appendDealerIds(collected, (preserved as any).dealerIds, seen);
        pushDealerId(collected, (preserved as any).dealerId, seen);
      }
    });
  }

  const orders = Array.isArray((shipment as any)?.orders)
    ? ((shipment as any).orders as Array<Record<string, any>>)
    : [];
  orders.forEach((order) => {
    if (!order || typeof order !== "object") return;
    appendDealerIds(collected, (order as any).dealerIds, seen);
    pushDealerId(collected, (order as any).dealerId, seen);
  });

  return collected;
};

export const normalizeOrderItemsSnapshot = (
  orderId: string,
  itemsSnapshot?: Record<string, ItemSnapshot> | null,
  logContext: string = "unknown"
): Record<string, ItemSnapshot> => {
  const startTime =
    typeof performance !== "undefined" ? performance.now() : Date.now();
  if (!itemsSnapshot) {
    console.debug("[SnapshotUtils] normalizeOrderItemsSnapshot: skip empty", {
      orderId,
      context: logContext,
    });
    return {};
  }

  const normalizedEntries = Object.entries(itemsSnapshot).map(([itemId, itemData]) => {
    const normalized: ItemSnapshot = {
      ...(itemData ?? ({} as ItemSnapshot)),
      id: itemId,
      orderId: itemData?.orderId || orderId,
    };

    return [itemId, normalized] as const;
  });

  const normalized = Object.fromEntries(normalizedEntries);
  const sampleKey = normalizedEntries[0]?.[0];
  const sampleValue = sampleKey ? normalized[sampleKey] : null;
  const duration =
    (typeof performance !== "undefined" ? performance.now() : Date.now()) -
    startTime;
  console.debug("[SnapshotUtils] normalizeOrderItemsSnapshot: complete", {
    orderId,
    context: logContext,
    itemCount: normalizedEntries.length,
    sample: sampleValue
      ? {
          id: sampleValue.id,
          orderId: sampleValue.orderId,
          status: sampleValue.status,
          hasOrderId: !!sampleValue.orderId,
        }
      : null,
    durationMs: Number(duration.toFixed(2)),
  });
  return normalized;
};

export const normalizeShipmentSnapshot = (
  shipment: Shipment,
  logContext: string = "unknown"
): Shipment => {
  const startTime =
    typeof performance !== "undefined" ? performance.now() : Date.now();
  const normalizedOrdersSnapshot: Record<string, OrderSnapshotWithPreserved> = {};
  try {
    console.debug("[SnapshotUtils] normalizeShipmentSnapshot: start", {
      context: logContext,
      shipmentId: shipment.id,
      ordersCount: Object.keys(shipment.ordersSnapshot || {}).length,
    });
  } catch (_err) {}

  Object.entries(shipment.ordersSnapshot || {}).forEach(([orderId, orderSnapshot]) => {
    if (!orderSnapshot) return;
    const snapshotWithPreserved = orderSnapshot as OrderSnapshotWithPreserved;
    const normalizedIncluded = normalizeOrderItemsSnapshot(
      orderId,
      snapshotWithPreserved.includedItemsSnapshot,
      `${logContext}:included`
    );
    const normalizedExcluded = normalizeOrderItemsSnapshot(
      orderId,
      snapshotWithPreserved.excludedItemsSnapshot,
      `${logContext}:excluded`
    );
    const preservedOrder = snapshotWithPreserved.preservedOrder
      ? {
          ...snapshotWithPreserved.preservedOrder,
          id: snapshotWithPreserved.preservedOrder.id || orderId,
          itemsSnapshot: snapshotWithPreserved.preservedOrder.itemsSnapshot
            ? normalizeOrderItemsSnapshot(
                snapshotWithPreserved.preservedOrder.id || orderId,
                snapshotWithPreserved.preservedOrder.itemsSnapshot,
                `${logContext}:preserved`
              )
            : undefined,
        }
      : undefined;
    normalizedOrdersSnapshot[orderId] = {
      ...snapshotWithPreserved,
      orderId: snapshotWithPreserved.orderId || orderId,
      includedItemsSnapshot: normalizedIncluded,
      excludedItemsSnapshot: normalizedExcluded,
      ...(preservedOrder ? { preservedOrder } : {}),
    };
    console.debug("[SnapshotUtils] normalizeShipmentSnapshot: order normalized", {
      context: logContext,
      shipmentId: shipment.id,
      orderId,
      includedCount: Object.keys(normalizedIncluded).length,
      excludedCount: Object.keys(normalizedExcluded).length,
      preserved: !!preservedOrder,
    });
  });

  const normalizedDealerIds = collectDealerIdsFromShipment(
    {
      ...shipment,
      ordersSnapshot: normalizedOrdersSnapshot,
    },
    { includeExisting: true }
  );
  const duration =
    (typeof performance !== "undefined" ? performance.now() : Date.now()) -
    startTime;
  console.debug("[SnapshotUtils] normalizeShipmentSnapshot: complete", {
    context: logContext,
    shipmentId: shipment.id,
    ordersCount: Object.keys(shipment.ordersSnapshot || {}).length,
    durationMs: Number(duration.toFixed(2)),
  });

  return {
    ...shipment,
    dealerIds: normalizedDealerIds,
    ordersSnapshot: normalizedOrdersSnapshot,
  };
};
